import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useProfiles } from "../settings/profileStore.js";

export type EpgLayoutMode = "overlay" | "traditional";
/**
 * Hours of programmes across the guide's width — what fits on screen before
 * scrolling: 2 (the default) or 4 (owner, 2026-10-05: "show 2 hours of shows
 * by default ... only 2 and 4 hours", "for shown on guide screen"). Until
 * then it was 4/12/24 and was also where the guide ENDED; how far it scrolls
 * is now GUIDE_REACH_HOURS whatever this says.
 */
export type EpgTimeWindow = 2 | 4;
/** Anything else saved (12h/24h, or 3h from before 4/12/24) falls back to the default. */
const VALID_TIME_WINDOWS: readonly number[] = [2, 4];
const DEFAULT_TIME_WINDOW: EpgTimeWindow = 2;
/**
 * How far ahead the guide scrolls and loads, from the top of the current hour
 * (owner, 2026-10-05: "36 hours should be enough to safely scroll"). The
 * server keeps 48 hours of listings (EPG_RETENTION_HOURS), so 36 is all there.
 */
export const GUIDE_REACH_HOURS = 36;
export type EpgOpacity = "light" | "medium" | "heavy";
export type EpgPreviewSize = "s" | "m" | "l";
export type EpgFontSize = "s" | "m" | "l";

export const OPACITY_MAP: Record<EpgOpacity, number> = { light: 0.45, medium: 0.62, heavy: 0.78 };
/**
 * Traditional-layout preview size, expressed as the fraction of the content
 * width (viewport minus the navrail) the VIDEO pane occupies; the info panel
 * takes the rest. The panel height is derived from this in CSS at 16:9, so the
 * video fills its pane instead of sitting letterboxed inside a fixed-height
 * box — the old fixed heights (180/260/360px) left black bars either side at
 * every size.
 */
const PREVIEW_WIDTH_MAP: Record<EpgPreviewSize, number> = { s: 0.5, m: 0.62, l: 0.75 };
/** Shared EPG text scale (Section 8 step 3): applied via --epg-font-scale. */
export const FONT_SCALE_MAP: Record<EpgFontSize, number> = { s: 0.85, m: 1, l: 1.2 };

export interface EpgSettings {
  layout: EpgLayoutMode;
  timeWindow: EpgTimeWindow;
  opacity: EpgOpacity;
  previewSize: EpgPreviewSize;
  /** Shared EPG font size across both layouts. */
  fontSize: EpgFontSize;
  /** Active category ID or null = all categories expanded. */
  activeCategoryId: string | null;
  /**
   * Whether the viewer has ever picked a category. Until then the guide opens
   * on the first one in the list (USA Ultra HD first) rather than "All
   * categories" — every channel at once, heavy on a 1 GB box and a poor first
   * look (fresh install on the projector, 2026-09-27). Picking "All" is then
   * a choice, and kept.
   */
  categoryChosen: boolean;
  collapsedCategories: Set<string>;
  /**
   * The channel last previewed in the guide. Coming back to the guide with
   * nothing playing reopens on it and previews it again (owner, 2026-09-25:
   * "a save state for the guide so whenever you go to it, it is the same
   * category and playing the same preview").
   */
  lastChannelId: string | null;
  /**
   * True until the guide has picked its opening category this session: set
   * at app start and on every profile switch, never saved (owner,
   * 2026-09-29: Favorites first when the profile has any; see
   * `openingCategory`).
   */
  openingPending: boolean;
}

/**
 * The guide's save state: its category, the channel it was previewing, its
 * layout and how many hours it shows. Each profile keeps its own (owner,
 * 2026-10-06: "the guide save state is carrying between profiles", then "make
 * layout and hours per profile too"); shade, preview size and text size stay
 * the device's.
 */
type GuideSaveState = Pick<EpgSettings, "activeCategoryId" | "categoryChosen" | "collapsedCategories" | "lastChannelId" | "layout" | "timeWindow">;
/** Saved form: the collapsed set as an array. */
type SavedGuideState = Omit<GuideSaveState, "collapsedCategories"> & { collapsedCategories: string[] };

const FRESH_GUIDE: SavedGuideState = {
  activeCategoryId: null,
  categoryChosen: false,
  collapsedCategories: [],
  lastChannelId: null,
  layout: "traditional",
  timeWindow: DEFAULT_TIME_WINDOW,
};

export interface EpgSettingsStore extends EpgSettings {
  /** The OTHER profiles' save states; the active profile's are the fields above. */
  guideByProfile: Record<string, SavedGuideState>;
  /** Computed: overlay background opacity value (0-1). */
  overlayOpacity: () => number;
  /** Computed: fraction of the content width taken by the traditional-layout
   *  video pane (0-1). The panel height follows from it at 16:9 in CSS. */
  previewWidthFraction: () => number;
  setLayout: (layout: EpgLayoutMode) => void;
  setTimeWindow: (hours: EpgTimeWindow) => void;
  setOpacity: (opacity: EpgOpacity) => void;
  setPreviewSize: (size: EpgPreviewSize) => void;
  setFontSize: (size: EpgFontSize) => void;
  /** Select the active category (shown in the guide). null = all channels flat. */
  setActiveCategory: (categoryId: string | null) => void;
  /** The viewer picking a category (the dropdown): remembered as their choice. */
  chooseCategory: (categoryId: string | null) => void;
  toggleCategory: (categoryId: string) => void;
  setLastChannelId: (channelId: string | null) => void;
  /** The guide has opened on its category for this session or profile. */
  openingDone: () => void;
}

export const useEpgSettings = create<EpgSettingsStore>()(
  persist(
    (set, get) => ({
      // Traditional is the default on every device: it is what most viewers
      // expect, and it reads properly at phone width. Overlay stays one tap
      // away in Settings → Guide (it left the guide toolbar when that was cut
      // back to the category picker and the channel box), and a saved choice
      // always wins.
      layout: "traditional",
      timeWindow: DEFAULT_TIME_WINDOW,
      opacity: "medium",
      previewSize: "m",
      fontSize: "m",
      activeCategoryId: null,
      categoryChosen: false,
      collapsedCategories: new Set<string>(),
      lastChannelId: null,
      openingPending: true,
      guideByProfile: {},

      overlayOpacity: () => OPACITY_MAP[get().opacity],
      previewWidthFraction: () => PREVIEW_WIDTH_MAP[get().previewSize],

      setLayout: (layout) => set({ layout }),
      setTimeWindow: (timeWindow) => set({ timeWindow }),
      setOpacity: (opacity) => set({ opacity }),
      setPreviewSize: (previewSize) => set({ previewSize }),
      setFontSize: (fontSize) => set({ fontSize }),
      setActiveCategory: (activeCategoryId) => set({ activeCategoryId }),
      chooseCategory: (activeCategoryId) => set({ activeCategoryId, categoryChosen: true }),
      setLastChannelId: (lastChannelId) => set({ lastChannelId }),
      openingDone: () => set({ openingPending: false }),
      toggleCategory: (categoryId) => {
        const prev = get().collapsedCategories;
        const next = new Set(prev);
        if (next.has(categoryId)) {
          next.delete(categoryId);
        } else {
          next.add(categoryId);
        }
        set({ collapsedCategories: next });
      },
    }),
    {
      name: "epg-settings",
      // 1: the 2-hour default (2026-10-05). Every install saved the old default,
      // 4, without choosing it, so the saved window is dropped once and the guide
      // opens on 2 hours; a later pick of 4 is kept.
      version: 1,
      migrate: (persisted, version) => {
        const state = (persisted ?? {}) as Record<string, unknown>;
        if (version < 1) delete state.timeWindow;
        // The saved shape (sets as arrays, no functions), which merge reads below.
        return state as never;
      },
      partialize: (state) => ({
        ...state,
        collapsedCategories: [...state.collapsedCategories],
        // Exclude functions from serialization
        overlayOpacity: undefined,
        previewWidthFraction: undefined,
        setLayout: undefined,
        setTimeWindow: undefined,
        setOpacity: undefined,
        setPreviewSize: undefined,
        setFontSize: undefined,
        setActiveCategory: undefined,
        chooseCategory: undefined,
        toggleCategory: undefined,
        setLastChannelId: undefined,
        openingPending: undefined,
        openingDone: undefined,
      }),
      merge: (persisted, current) => {
        const p = persisted as Record<string, unknown>;
        return {
          ...current,
          layout: (p.layout as EpgLayoutMode) ?? current.layout,
          // A window that is not 2 or 4 (12h/24h saved before 2026-10-05) falls
          // back to the 2h default rather than a broken value.
          timeWindow: (VALID_TIME_WINDOWS.includes(p.timeWindow as number)
            ? (p.timeWindow as EpgTimeWindow)
            : DEFAULT_TIME_WINDOW),
          opacity: (p.opacity as EpgOpacity) ?? current.opacity,
          previewSize: (p.previewSize as EpgPreviewSize) ?? current.previewSize,
          fontSize: (p.fontSize as EpgFontSize) ?? current.fontSize,
          collapsedCategories: Array.isArray(p.collapsedCategories)
            ? new Set(p.collapsedCategories as string[])
            : current.collapsedCategories,
          // Saved all along but never read back, so the guide forgot its
          // category on every launch (2026-09-25). A field left out of this
          // list is dropped on load, however faithfully it is saved.
          activeCategoryId: typeof p.activeCategoryId === "string" ? p.activeCategoryId : current.activeCategoryId,
          // Installs from before the flag: a saved category was a choice.
          categoryChosen: typeof p.categoryChosen === "boolean" ? p.categoryChosen : typeof p.activeCategoryId === "string",
            lastChannelId: typeof p.lastChannelId === "string" ? p.lastChannelId : current.lastChannelId,
          guideByProfile: p.guideByProfile && typeof p.guideByProfile === "object" ? (p.guideByProfile as Record<string, SavedGuideState>) : {},
        };
      },
    },
  ),
);

// A profile switch puts the old profile's place in the guide away and brings out
// the new one's (a new profile starts fresh), and opens the guide on the new
// profile's Favorites next time. A removed profile's place goes with it.
useProfiles.subscribe((state, previous) => {
  const guide = useEpgSettings.getState();
  const kept = Object.fromEntries(Object.entries(guide.guideByProfile).filter(([id]) => state.profiles.some((p) => p.id === id)));
  if (state.activeProfileId === previous.activeProfileId) {
    if (Object.keys(kept).length !== Object.keys(guide.guideByProfile).length) useEpgSettings.setState({ guideByProfile: kept });
    return;
  }
  if (previous.profiles.some((p) => p.id === previous.activeProfileId) && state.profiles.some((p) => p.id === previous.activeProfileId)) {
    kept[previous.activeProfileId] = {
      activeCategoryId: guide.activeCategoryId,
      categoryChosen: guide.categoryChosen,
      collapsedCategories: [...guide.collapsedCategories],
      lastChannelId: guide.lastChannelId,
      layout: guide.layout,
      timeWindow: guide.timeWindow,
    };
  }
  const next = kept[state.activeProfileId] ?? FRESH_GUIDE;
  delete kept[state.activeProfileId];
  useEpgSettings.setState({
    activeCategoryId: next.activeCategoryId,
    categoryChosen: next.categoryChosen,
    collapsedCategories: new Set(next.collapsedCategories),
    lastChannelId: next.lastChannelId,
    // Saved before layout and hours were per profile: the device's choice so far.
    layout: next.layout ?? guide.layout,
    timeWindow: VALID_TIME_WINDOWS.includes(next.timeWindow) ? next.timeWindow : guide.timeWindow,
    guideByProfile: kept,
    openingPending: true,
  });
});
